import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LoadThemeComponent from '../../../base/LoadThemeComponent';
import { formatCurrency } from '../../../utils/formatCurrency';
import NumberOfDrawsSelect from '../Pick/options/NumberOfDrawsSelect';
import { LineNumbers } from '../Types/Types';
import DrawCounter from '../elements/DrawCounter';
import HiddenInputs from '../elements/HiddenInputs';
import Purchase from '../elements/Purchase';
import PurchaseConfirmation from '../elements/PurchaseConfirmation';
import PurchaseMoreLessButtons from '../elements/PurchaseMoreLessButtons';
import ResellerModule from '../elements/ResellerModule';
import SelectableLotteryDrawDatesModule from '../elements/SelectableLotteryDrawDatesModule';
import WinUpTo from '../elements/WinUpTo';
import useIsMobileUpdater from '../elements/useIsMobileUpdater';
import { getValidLines } from '../helpers/linesHelper';
import useLinesHelper from '../helpers/useLinesHelper';
import { RootState } from '../redux/store';
import { setDefaultStakes } from '../redux/storeSlice';

import KenoPicker from './picker/KenoPicker';
import StakeAndNumberPanel from './picker/StakeAndNumberPanel';

import PurchasePromoCode from 'components/promo-codes/PurchasePromoCode';
import { isDefault, isTemplateDelta } from 'utils/template';

export default function KenoPlay() {
    const { t } = useTranslation();
    useIsMobileUpdater();
    const dispatch = useDispatch();
    const { lottery, lines, isMobile, selectableDrawDateTimes, resellerOptionsEnabled, linePriceGross } = useSelector(
        (state: RootState) => state.playStore
    );
    const { numbersToPlay } = useSelector((state: RootState) => state.kenoPlayStore);
    const [validLines, setValidLines] = useState<LineNumbers[]>([]);
    const { removeAllLines } = useLinesHelper();

    useEffect(() => {
        const newValidLines = getValidLines(lottery, lines, [numbersToPlay]);
        setValidLines(newValidLines);
        dispatch(setDefaultStakes(newValidLines.length));
    }, [lines, numbersToPlay]);

    const availablePurchaseOptions = [
        {
            key: 'selectableDrawDates',
            component: <SelectableLotteryDrawDatesModule />,
            isVisible: selectableDrawDateTimes.length,
        },
        {
            key: 'resellerModule',
            component: <ResellerModule />,
            isVisible: resellerOptionsEnabled,
        },
        {
            key: 'numberOfDraws',
            component: <NumberOfDrawsSelect />,
            isVisible: lottery.isMultiDrawEnabled,
        },
    ].filter((option) => option.isVisible);

    const baseView = () => (
        <div className="keno-play">
            <PurchaseConfirmation />
            <HiddenInputs lines={validLines} />
            <div className="myaccount-play keno mb-5">
                <div className="w-100 purchase-options">
                    <div className="row">
                        <div className="col-md-6 col-12 mb-md-5 mb-4 px-sm-4 px-0 purchase-opt-col">
                            <DrawCounter isMobile={isMobile} />
                        </div>
                        <div className="col-md-6 col-12 mb-md-5 mb-4 px-sm-4 px-0 purchase-opt-col">
                            <StakeAndNumberPanel />
                        </div>
                        <div className="col-12 mb-md-5 mb-4 px-sm-4 px-0 purchase-opt-col">
                            <WinUpTo />
                        </div>
                    </div>
                </div>
                <KenoPicker />
            </div>
            {!isMobile && isTemplateDelta() && <PurchaseMoreLessButtons className="mb-5" />}
            {lottery.isMultiDrawEnabled && (
                <div className="w-100 purchase-options">
                    <div className="row">
                        <div className="col-12 purchase-opt-col user-guide-play-ticket-type">
                            <NumberOfDrawsSelect />
                        </div>
                    </div>
                </div>
            )}
            <PurchasePromoCode />
            <div className="row no-gutters w-100 d-block d-md-flex justify-content-between align-items-center">
                {!isMobile && !isTemplateDelta() && <PurchaseMoreLessButtons className="my-5 mr-5" />}
                <SelectableLotteryDrawDatesModule />
                <ResellerModule />
                <Purchase />
            </div>
        </div>
    );

    const defaultView = () => (
        <div className="keno-play">
            <PurchaseConfirmation />
            <HiddenInputs lines={validLines} />
            <div
                id="purchase-options"
                className="w-100 purchase-options purchase-options-pick user-guide-play-ticket-type pb-0"
            >
                {isDefault() && (
                    <div className="message-box d-flex justify-content-between">
                        <span>
                            {t('playPicker.modal.selectedLines', {
                                linePrice: formatCurrency(linePriceGross, 2, lottery.currencyCode),
                                selectedCount: lines.length - 1,
                            })}
                        </span>
                        <span className="clear-all fw-bold text-decoration-underline" onClick={() => removeAllLines()}>
                            {t('playPicker.clearAll')}
                        </span>
                    </div>
                    )
                }
                <StakeAndNumberPanel />
            </div>
            <KenoPicker />
            <div
                id="purchase-options"
                className="w-100 purchase-options purchase-options-pick user-guide-play-ticket-type"
            >
                <div className="row">
                    {availablePurchaseOptions.map((option, index) => {
                        const isLastSingleItem = index === availablePurchaseOptions.length - 1 && index % 2 === 0;
                        return (
                            <div key={option.key} className={`col-${isLastSingleItem ? '12' : '6'}`}>
                                {option.component}
                            </div>
                        );
                    })}
                </div>
            </div>
            <Purchase />
        </div>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}
